import { useEffect, useState } from 'react';
import Pagination from './Pagination';
import DateRangePicker from './DateRangePicker';
import { useAuth } from './AuthContext';
import {
  getCallStatus,
  formatTimestamp,
  filterCallsByDate,
  groupByCustomer,
  getUnansweredStreak,
  getCustomerNumber,
} from './callUtils';

// Same trailing-10-digit rule the ERP's own phone matching uses (see the 
// ERP's phone_utils.normalize_phone) — orders_by_phone from
// POST /api/ivr/erp/recent-orders is keyed this way, so a customer number
// has to be reduced the same way to ever match it.
const last10Digits = (raw) => {
  const digits = String(raw || '').replace(/\D/g, '');
  return digits.length >= 10 ? digits.slice(-10) : null;
};

const DEFAULT_PAGE_SIZE = 50;

// The three DTMF options currently wired up in Tata Smartflo (see the
// "DTMF Option" webhooks registered there, e.g. "RRP IVR Press 3 Other
// Assistance") — shown as their own stat card even before any call has
// pressed them yet, so the page isn't empty on day one. Any digit that shows
// up in the data but isn't one of these (a new option added later in Tata,
// before this list is updated) still gets its own card automatically — see
// `digitsToShow` below.
const KNOWN_DIGITS = ['1', '2', '3'];

// Every call arrives on the same webhook (POST /api/webhook/ivr) keyed by
// uuid — a DTMF press merges into that call's existing row (see upsertCall in
// server/db.js), so digit_pressed here is just a normal field on `calls`,
// same as any other. No separate fetch needed.
function IvrPressStats({ calls }) {
  const { authFetch } = useAuth();
  const [digitFilter, setDigitFilter] = useState('All');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [ordersByPhone, setOrdersByPhone] = useState({});

  const dateFiltered = filterCallsByDate(calls, fromDate, toDate);
  const pressedCalls = dateFiltered.filter((call) => call.digit_pressed);

  // One representative IVR-menu name per digit (e.g. "Other Assistance"),
  // taken from whichever matching call happens to carry ivr_name — Tata
  // doesn't send it on every event, so this is a best-effort label, not
  // guaranteed to be present.
  const labelByDigit = new Map();
  for (const call of pressedCalls) {
    const digit = String(call.digit_pressed).trim();
    if (digit && call.ivr_name && !labelByDigit.has(digit)) {
      labelByDigit.set(digit, call.ivr_name);
    }
  }

  const digitsInData = new Set(
    pressedCalls.map((call) => String(call.digit_pressed).trim()).filter(Boolean)
  );
  const digitsToShow = [...new Set([...KNOWN_DIGITS, ...digitsInData])].sort((a, b) => {
    const na = Number(a);
    const nb = Number(b);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
    if (!Number.isNaN(na)) return -1;
    if (!Number.isNaN(nb)) return 1;
    return a.localeCompare(b);
  });

  // Grouped over EVERY call in range, not just the ones with a digit_pressed
  // — a click-to-call callback the agent makes afterward has no digit_pressed
  // of its own (it's a separate row), so group.latestCall/callsNewestFirst
  // here reflect the customer's true most recent event, including that
  // callback. Without this, a customer's Outcome stayed frozen at "Missed"
  // from their IVR press forever, even after an agent called them back and
  // they answered.
  const allGroupsByNumber = new Map(groupByCustomer(dateFiltered).map((g) => [g.customerNumber, g]));

  // One entry per customer across ALL their IVR presses (not scoped to a
  // single digit). `latestDigitCall` is their most recent call that actually
  // has a digit_pressed — kept separate from the group's true latestCall
  // above, since that could now be a callback with no digit/ivr_name at all —
  // so the IVR-option label and digit attribution still reflect a real press,
  // while Outcome/Agent below reflect what's actually happened since. A
  // customer who pressed 1 last week and 2 yesterday counts only once, under
  // Press 2: whichever digit they chose most recently is their current
  // intent, not a tally of every option they've ever tried.
  const customerGroups = [];
  const seenNumbers = new Set();
  for (const call of pressedCalls) {
    const number = getCustomerNumber(call);
    if (seenNumbers.has(number)) continue;
    seenNumbers.add(number);
    customerGroups.push({ ...allGroupsByNumber.get(number), latestDigitCall: call });
  }

  const countForDigit = (digit) =>
    customerGroups.filter((group) => String(group.latestDigitCall.digit_pressed).trim() === digit).length;

  // Filtering by digit here means "customers whose most recent PRESS was this
  // digit" (matching countForDigit above), so the card's number and its
  // drill-down table always agree — even though the row itself may now show
  // a later callback's outcome.
  const groupedRows =
    digitFilter === 'All'
      ? customerGroups
      : customerGroups.filter((group) => String(group.latestDigitCall.digit_pressed).trim() === digitFilter);

  const totalPages = Math.max(1, Math.ceil(groupedRows.length / pageSize));

  useEffect(() => {
    setPage(1);
  }, [digitFilter, fromDate, toDate]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  const pageRows = groupedRows.slice((page - 1) * pageSize, page * pageSize);
  const hasDateFilter = Boolean(fromDate || toDate);

  // Cross-checks the ERP for each visible customer's latest order, so the
  // table can show "did this IVR-press caller actually order?" instead of
  // just the raw digit they pressed. Scoped to whatever's on the current
  // page (not every pressed call ever) to keep this a small batch lookup —
  // same batching pattern as AbandonedCarts.js's already-ordered check, just
  // fetched here instead of folded into the calls list itself.
  const pageRowsKey = pageRows.map((group) => group.customerNumber).join(',');
  useEffect(() => {
    const phones = [...new Set(pageRows.map((group) => group.customerNumber).filter(Boolean))];
    if (phones.length === 0) {
      setOrdersByPhone({});
      return undefined;
    }
    let cancelled = false;
    authFetch('/api/ivr/erp/recent-orders', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phones }),
    })
      .then((response) => response.json())
      .then((body) => {
        if (!cancelled) setOrdersByPhone(body?.orders_by_phone || {});
      })
      .catch(() => {
        if (!cancelled) setOrdersByPhone({});
      });
    return () => {
      cancelled = true;
    };
  }, [pageRowsKey]);

  const orderNumberForCustomer = (customerNumber) => {
    const key = last10Digits(customerNumber);
    return (key && ordersByPhone[key]?.order_number) || null;
  };

  return (
    <>
      <div className="incoming-filters-bar">
        <div className="agent-date-filter">
          <DateRangePicker
            fromDate={fromDate}
            toDate={toDate}
            onApply={(from, to) => {
              setFromDate(from);
              setToDate(to);
            }}
          />
          {hasDateFilter && (
            <button
              type="button"
              className="agent-date-filter-clear"
              onClick={() => {
                setFromDate('');
                setToDate('');
              }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="stat-cards">
        <button
          type="button"
          className={`stat-card ${digitFilter === 'All' ? 'active' : ''}`}
          onClick={() => setDigitFilter('All')}
        >
          <div className="stat-value">{customerGroups.length}</div>
          <div className="stat-label">All IVR Presses</div>
        </button>
        {digitsToShow.map((digit) => (
          <button
            type="button"
            key={digit}
            className={`stat-card ${digitFilter === digit ? 'active' : ''}`}
            onClick={() => setDigitFilter(digit)}
          >
            <div className="stat-value">{countForDigit(digit)}</div>
            <div className="stat-label">
              Press {digit}
              {labelByDigit.get(digit) ? ` · ${labelByDigit.get(digit)}` : ''}
            </div>
          </button>
        ))}
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Date/Time</th>
              <th>Caller Number</th>
              <th>Order Number</th>
              <th>IVR Option</th>
              <th>Outcome</th>
              <th>Agent</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center' }}>
                  {pressedCalls.length === 0
                    ? 'No IVR menu presses recorded yet.'
                    : 'No calls match this filter.'}
                </td>
              </tr>
            ) : (
              pageRows.map((group) => {
                const pressCall = group.latestDigitCall;
                const latestCall = group.latestCall;
                const status = getCallStatus(latestCall);
                const unansweredStreak = status !== 'Answered' ? getUnansweredStreak(group) : 0;
                return (
                  <tr key={group.customerNumber}>
                    <td>{formatTimestamp(pressCall.start_stamp)}</td>
                    <td>
                      {group.customerNumber}
                      {unansweredStreak > 1 && <span className="call-count"> ({unansweredStreak})</span>}
                    </td>
                    <td className="table-cell-strong">
                      {orderNumberForCustomer(group.customerNumber) || '-'}
                    </td>
                    <td>{pressCall.ivr_name || '-'}</td>
                    <td>
                      <span className={`status ${status.toLowerCase()}`}>{status}</span>
                    </td>
                    <td>{latestCall.answered_agent_name || '-'}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        totalItems={groupedRows.length}
      />
    </>
  );
}

export default IvrPressStats;
